import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { GoogleMap } from "@angular/google-maps";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import dayjs from "dayjs";
import { Subscription } from "rxjs";
import {
  BrandCampaign,
  BrandCampaignStatus,
} from "../../../shared/models/brandCampaign";
import { BrandCampaignCalendar } from "../../../shared/models/brandCampaignCalendar";
import {
  CampaignBudgetType,
  CampaignCurrency,
  CampaignCurrencySymbol,
  CampaignDatesType,
  CampaignType,
  CtaText,
  Gender,
  GoogleAdsBiddingStrategyType,
  PaymentType,
} from "../../../shared/enums/campaign.enums";
import { CampaignPostContentType } from "../../../shared/enums/campaignPost.enums";
import { FacebookTargetingCategory } from "../../../shared/models/facebookTargeting";
import { BrandService } from "../../../shared/services/api/brand.service";
import { BrandUrl } from "../../brand.url";
import { FacebookTargetingService } from "../shared/services/facebook-targeting.service";
import {
  GoogleTargetingService,
  IGoogleAudienceData,
} from "../shared/services/google-targeting.service";
import { NotificationService } from "../../../shared/services/notification.service";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";
import { BrandCampaignPromoAdsPage } from "./brand-campaign-promo-ads-page";
import { PlacementType } from "./brand-campaign-promo-ads-whitelist-dialog/brand-campaign-promo-ads-whitelist-dialog.component";
import { BrandCampaignDialogService } from "../brand-campaign-dialog.service";
import { getDateFormatSignal } from "../../../shared/services/date.service";
import { MatCheckboxChange } from "@angular/material/checkbox";

const MAX_BUDGET = 1000;

@Component({
  selector: "app-brand-campaign-promo-ads",
  templateUrl: "./brand-campaign-promo-ads.component.html",
  styleUrl: "./brand-campaign-promo-ads.component.scss",
})
export class BrandCampaignPromoAdsComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy
{
  @ViewChild(GoogleMap)
  private map!: GoogleMap;

  protected readonly BrandCampaignStatus = BrandCampaignStatus;
  protected readonly CAMPAIGN_CUSTOM_DATES_TYPE = CampaignDatesType.CustomDates;
  protected readonly CAMPAIGN_SAME_DATES_TYPE = CampaignDatesType.SameDates;
  protected readonly CAMPAIGN_TYPE_DOWNLOAD_ONLY = CampaignType.DownloadOnly;
  protected readonly CampaignBudgetTypes = CampaignBudgetType;
  protected readonly CampaignCurrency = CampaignCurrency;
  protected readonly CtaText = CtaText;
  protected readonly Gender = Gender;
  protected readonly googleAdsBiddingStrategyOptions: string[] = Object.values(
    GoogleAdsBiddingStrategyType,
  );
  protected readonly maxRadiusDistance = 80;
  protected readonly minRadiusDistance = 10;
  protected readonly PaymentType = PaymentType;
  protected readonly PlacementType = PlacementType;
  protected readonly PromoAdsPage = BrandCampaignPromoAdsPage;
  protected readonly ageRanges: { value: string; name: string }[] = [
    {
      value: "18-24",
      name: "18-24",
    },
    {
      value: "25-34",
      name: "25-34",
    },
    {
      value: "35-44",
      name: "35-44",
    },
    {
      value: "45-54",
      name: "45-54",
    },
    {
      value: "55-64",
      name: "55-64",
    },
    {
      value: "65",
      name: "65+",
    },
  ];

  protected adsEndDate!: Date;
  protected adsStartDate!: Date;
  protected campaign!: BrandCampaign;
  protected dateFormat = getDateFormatSignal();
  protected facebookTargetingCategories: FacebookTargetingCategory[] = [];
  protected googleAdsPanelOpenState = false;
  protected googleTargetingOptions?: IGoogleAudienceData;
  protected isBoostPostAdTypeDisabled = false;
  protected isEditable = false;
  protected loadingGoogleAdsTargeting = false;
  protected loadingPage = false;
  protected mapCenter?: google.maps.LatLng;
  protected markers: google.maps.LatLng[] = [];
  protected maxAdDuration = 14;
  protected maxAmountOfAds = 20;
  protected maxBudget = MAX_BUDGET;
  protected maxDate!: Date;
  protected maxGoogleAdGroupBid = 5;
  protected minDate!: Date;
  protected minGoogleAdGroupBid = 0.5;
  protected page = BrandCampaignPromoAdsPage.Settings;
  protected panelOpenState = false;
  protected placement = new FormControl<PlacementType>(PlacementType.Blacklist);
  protected saving = false;
  protected savingError = false;

  private readonly subscriptions = new Subscription();
  private previousAdsStartDate!: Date;
  private previousAdsEndDate!: Date;

  constructor(
    public readonly brandCampaignService: BrandCampaignService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly brandCampaignDialogService: BrandCampaignDialogService,
    private readonly brandService: BrandService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly facebookTargetingService: FacebookTargetingService,
    private readonly googleTargetingService: GoogleTargetingService,
    private readonly notificationService: NotificationService,
    private readonly router: Router,
    private readonly translateService: TranslateService,
  ) {
    this.initData(this.brandCampaignService.currentBrandCampaign);
  }

  public async ngOnInit(): Promise<void> {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.page = params.page
        ? params.page
        : BrandCampaignPromoAdsPage.Settings;
    });

    this.initData(this.campaign);
    this.setSubscribers();

    this.googleTargetingService
      .getGoogleTargeting()
      .subscribe((googleTargetingOptions) => {
        this.googleTargetingOptions = googleTargetingOptions;
      });

    this.facebookTargetingService
      .getFacebookTargetingCategories()
      .subscribe((facebookTargetingCategories) => {
        this.facebookTargetingCategories = facebookTargetingCategories;
      });

    this.checkDefaultGoogleAdsObjective();
  }

  public ngAfterViewInit(): void {
    this.googleMapsInit();
  }

  public ngAfterViewChecked(): void {
    // Important to avoid selects breaking on language change
    this.cdRef.detectChanges();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private googleMapsInit(): void {
    const brand = this.brandService.currentBrand;
    let lat: number;
    let lng: number;

    if (brand.address && brand.address.latitude && brand.address.longitude) {
      lat = parseFloat(brand.address.latitude + "");
      lng = parseFloat(brand.address.longitude + "");
    } else {
      lat = 48.1387;
      lng = 11.5784;
    }

    if (google?.maps) {
      this.mapCenter = new google.maps.LatLng(lat, lng);
      this.markers.push(this.mapCenter);
    }

    if (this.map && this.mapCenter) {
      this.map.panTo(this.mapCenter);
    }
  }

  private initData(campaign: BrandCampaign): void {
    this.campaign = campaign;
    this.adsStartDate = campaign.fbCampaignStartDate
      ? campaign.fbCampaignStartDate
      : campaign.startDate;
    this.adsEndDate = campaign.fbCampaignEndDate
      ? campaign.fbCampaignEndDate
      : campaign.endDate;
    this.previousAdsStartDate = this.adsStartDate;
    this.previousAdsEndDate = this.adsEndDate;
    this.isEditable = campaign.isEditable;
    this.minDate = campaign.startDate;
    this.maxDate = campaign.endDate;
    this.maxBudget = Math.max(MAX_BUDGET, campaign.budget ?? 0);

    // Boosted post do not work now with 360 or GIF
    this.campaign.currentDetails.categories.map((c) => {
      if (!this.isBoostPostAdTypeDisabled) {
        this.isBoostPostAdTypeDisabled =
          c.posts.filter(
            (post) =>
              post.mediaType === CampaignPostContentType.Gif ||
              post.mediaType === CampaignPostContentType.Image360,
          ).length > 0;
      }
    });
    this.campaign.currentDetails.calendars.map(
      (calendar: BrandCampaignCalendar) => {
        if (!this.isBoostPostAdTypeDisabled) {
          this.isBoostPostAdTypeDisabled =
            calendar.calendarPosts.filter(
              (calendarPost) => calendarPost.isDarkPost,
            ).length > 0;
        }
      },
    );
  }

  protected genderChanged(event: MatCheckboxChange): void {
    switch (this.campaign.gender) {
      case Gender.Both:
        this.campaign.gender =
          event.source.value === Gender.Male ? Gender.Female : Gender.Male;
        break;
      case Gender.Female:
        this.campaign.gender =
          event.source.value === Gender.Male ? Gender.Both : (null as any);
        break;
      case Gender.Male:
        this.campaign.gender =
          event.source.value === Gender.Female ? Gender.Both : (null as any);
        break;
      case null:
        this.campaign.gender =
          event.source.value === Gender.Male ? Gender.Male : Gender.Female;
        break;
    }

    this.updateCampaign();
  }

  protected updateCampaignCurrency(): void {
    this.campaign.currencySymbol =
      CampaignCurrencySymbol[this.campaign.currency];
    this.updateCampaign();
  }

  protected updateCampaign(): void {
    this.saving = true;
    this.checkDefaultGoogleAdsObjective();

    this.brandCampaignService.updateBrandCampaign(this.campaign).subscribe({
      next: (campaign) => {
        this.saving = false;
        this.savingError = false;

        if (
          (this.placement.value === PlacementType.Whitelist &&
            campaign.googleAdsWhiteListPlacements.length <
              this.campaign.googleAdsWhiteListPlacements.length) ||
          (this.placement.value === PlacementType.Blacklist &&
            campaign.googleAdsBlackListPlacements.length <
              this.campaign.googleAdsBlackListPlacements.length)
        ) {
          this.notificationService.warning(
            this.translateService.instant(
              "brand.campaign.promoAds.placements.alertDeletedWhitelist",
              { placementType: this.placement.value },
            ),
          );
        }

        this.campaign = campaign;
      },
      error: () => {
        this.notificationService.error("shared.changesCouldNotBeSaved");
        this.saving = false;
        this.savingError = true;
      },
    });
  }

  // this might be wrong! TODO FRONTEND-7564 find out https://app.asana.com/0/1202486247410328/1204726404757564/f
  private validateCampaignDates(startDate: Date, endDate: Date): boolean {
    if (!startDate || !endDate) {
      this.notificationService.error("shared.enterAValidDate");
      return false;
    }

    if (startDate >= endDate) {
      this.notificationService.error(
        "brand.campaign.promoAds.startAfterEndDateError",
      );
      this.adsStartDate = this.previousAdsStartDate;
      this.adsEndDate = this.previousAdsEndDate;
      return false;
    }

    return true;
  }

  protected validateAndUpdateCampaignStartDate(
    newStartDate: dayjs.Dayjs,
  ): void {
    const startDate = newStartDate.startOf("day").toDate();

    if (this.validateCampaignDates(startDate, this.adsEndDate)) {
      this.adsStartDate = startDate;
      this.previousAdsStartDate = startDate;
      this.campaign.fbCampaignStartDate = startDate;
      this.updateCampaign();
    }
  }

  protected validateAndUpdateCampaignEndDate(newEndDate: dayjs.Dayjs): void {
    const endDate = newEndDate.endOf("day").toDate();

    if (this.validateCampaignDates(this.adsStartDate, endDate)) {
      this.adsEndDate = endDate;
      this.previousAdsEndDate = endDate;
      this.campaign.fbCampaignEndDate = endDate;
      this.updateCampaign();
    }
  }

  protected isGoogleConfigEnabled(): boolean {
    return (
      !!this.campaign &&
      this.campaign.isEditable &&
      !this.campaign.isDownloadOnly
    );
  }

  protected isAdTypeAdWithLinkDisabled(): boolean {
    if (!this.campaign) {
      return true;
    }

    return !this.isEditable || this.campaign.type === CampaignType.DownloadOnly;
  }

  protected nextPage(): void {
    this.page = BrandCampaignPromoAdsPage.MarketingChannels;
    this.navigateTo(this.page);
  }

  protected previousPage(): void {
    this.page = BrandCampaignPromoAdsPage.Settings;
    this.navigateTo(this.page);
  }

  private navigateTo(page: BrandCampaignPromoAdsPage): void {
    this.loadingPage = true;
    const url =
      page === BrandCampaignPromoAdsPage.Settings
        ? BrandUrl.Campaign.AdsSettings(
            this.brandService.currentBrandId,
            this.campaign.id,
          )
        : BrandUrl.Campaign.AdsChannels(
            this.brandService.currentBrandId,
            this.campaign.id,
          );

    this.router.navigateByUrl(url);
    this.brandCampaignService.promoAdsTabChangedSource.next(page);

    setTimeout(() => {
      this.loadingPage = false;
    }, 2000);
  }

  protected datesTypeChanged(event: MatCheckboxChange): void {
    this.campaign.adDatesType = event.source.value as CampaignDatesType;

    if (this.campaign.adDatesType === this.CAMPAIGN_SAME_DATES_TYPE) {
      this.campaign.fbCampaignStartDate = this.campaign.startDate;
      this.campaign.fbCampaignEndDate = this.campaign.endDate;
      this.adsStartDate = this.campaign.startDate;
      this.adsEndDate = this.campaign.endDate;
    }

    this.updateCampaign();
  }

  protected hasAgeRange(range: string): boolean {
    return this.campaign.ageRanges.includes(range);
  }

  protected ageRangeChanged(ageRange: string): void {
    if (this.hasAgeRange(ageRange)) {
      this.campaign.ageRanges.map((ar, idx) => {
        if (ar === ageRange) {
          this.campaign.ageRanges.splice(idx, 1);
        }
      });
    } else {
      this.campaign.ageRanges.push(ageRange);
    }

    this.updateCampaign();
  }

  protected paymentTypePercentageChange(value: number): void {
    this.campaign.paymentTypePercentage = value;
  }

  protected toggleGoogleAdsPanel(): void {
    if (!this.googleAdsPanelOpenState) {
      this.loadingGoogleAdsTargeting = true;

      setTimeout(() => {
        this.loadingGoogleAdsTargeting = false;
      }, 2000);
    }

    this.googleAdsPanelOpenState = !this.googleAdsPanelOpenState;
  }

  protected async addPlacement(type: PlacementType): Promise<void> {
    const placements =
      type === PlacementType.Blacklist
        ? this.campaign.googleAdsBlackListPlacements
        : this.campaign.googleAdsWhiteListPlacements;

    const result = await this.brandCampaignDialogService.showPromoAdsWhitelist({
      type: type,
      placements: [...placements],
    });

    if (result) {
      if (type === PlacementType.Blacklist) {
        this.campaign.googleAdsBlackListPlacements = result;
      } else if (type === PlacementType.Whitelist) {
        this.campaign.googleAdsWhiteListPlacements = result;
      }

      this.updateCampaign();
    }
  }

  private setSubscribers(): void {
    this.subscriptions.add(
      this.brandCampaignService.currentBrandCampaign$.subscribe((campaign) => {
        if (campaign) {
          this.initData(campaign);
        }
      }),
    );
  }

  private checkDefaultGoogleAdsObjective(): void {
    if (
      this.campaign.useGoogleAds &&
      !Object.values(GoogleAdsBiddingStrategyType).includes(
        this.campaign.googleAdsBiddingStrategyType,
      )
    ) {
      this.campaign.googleAdsBiddingStrategyType =
        GoogleAdsBiddingStrategyType.TargetSpend;
    }
  }

  public get getMinAmountOfAds(): number {
    return (
      Math.min(
        this.campaign.maxAdsCampaignPartner,
        this.campaign.totalAdsInCalendar,
      ) || 0
    );
  }
}
